import { Route } from '@react-navigation/native';
import { theme } from 'aimo-ui';
import config from 'config/config';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { enUS, fi, nb, sv } from 'date-fns/locale';
import i18n from 'i18next';
import { Share } from 'react-native';
import ReactNativeBlobUtil from 'react-native-blob-util';
import { getVersion } from 'react-native-device-info';
import {
  AddressProps,
  BillingFrequency,
  BillingItem,
  CompanyBenefit,
  CountryCode,
  Lang,
  Maybe,
  ParkZone,
  Pricing,
  Vehicle,
  VehicleCountryCode,
} from 'types/generatedSchemaTypes';
import { ThrottleTimer } from 'types/states';
import Logger from 'utils/Logger';
import { isAndroid, isIos, isWeb } from 'utils/deviceUtils';
import { isServiceFeeType } from 'utils/paymentUtils';
import { version as packageJsonVersion } from '../../package.json';
import { parseFeatureFlagObject } from './featureFlagUtils';

export type CurrencyProps = 'EUR' | 'NOK' | 'SEK';

interface LangMapProps {
  [index: string]: Locale;
}

const langMap: LangMapProps = {
  fi,
  en: enUS,
  no: nb,
  sv,
};

export const fallbackCountry = 'Finland';
export const fallbackCountryCode = 'FI';

export const getLocale = () => langMap[i18n.language] || enUS;

export const getLang = () =>
  (i18n?.language?.toLowerCase() as Lang) || ('en' as Lang);

export const getCurrencyLocales = (country: CountryCode = 'FI') => {
  const currencyLocales = {
    locales: 'fi-FI',
    currency: 'EUR',
  };

  if (country === 'SE') {
    currencyLocales.locales = 'sv-SE';
    currencyLocales.currency = 'SEK';
  }
  if (country === 'NO') {
    currencyLocales.locales = 'no-NO';
    currencyLocales.currency = 'NOK';
  }
  return currencyLocales;
};

// TODO: remove this when RN 0.65 is released
export const getCurrencyString = (country: CountryCode = 'FI') => {
  if (country === 'SE') {
    return 'kr';
  }
  if (country === 'NO') {
    return 'kr';
  }
  return '€';
};

export const getDistanceText = (
  distanceInKms: number,
  countryCode: CountryCode
) => {
  return distanceInKms >= 1
    ? `${numberFormatter(
        Number(Math.round(Number(distanceInKms + 'e1')) + 'e-1'),
        countryCode
      )} km`
    : `${numberFormatter(Math.round(distanceInKms * 1000), countryCode)} m`;
};

export const getCorrectBillingItem = (
  value?: BillingItem[]
): BillingItem[] | undefined =>
  value && value.length > 0
    ? value.filter((data) => !isServiceFeeType(data.type))
    : undefined;

export const currencyFormatter = (
  value: number,
  country?: CountryCode,
  withDecimal?: boolean
) => {
  const formatter = new global.Intl.NumberFormat(
    getCurrencyLocales(country)?.locales,
    {
      style: 'currency',
      currency: getCurrencyLocales(country)?.currency,
      minimumFractionDigits: withDecimal ? 2 : 0,
      maximumFractionDigits: 2,
    }
  );
  let formattedValue = formatter.format(value);
  formattedValue = formattedValue.replace(/\s/g, ' ');
  return formattedValue;
};

export const numberFormatter = (value: number, country: CountryCode) => {
  const formatter = new global.Intl.NumberFormat(
    getCurrencyLocales(country)?.locales
  );
  return formatter.format(value);
};
export class Localized {
  private static fallbackLocaleCode = 'en-US';
  static readonly dateTime = (
    date?: Maybe<Date> | number | string,
    locales?: string | string[],
    options: Intl.DateTimeFormatOptions = {}
  ) => {
    if (!locales) {
      locales = getLocale().code || this.fallbackLocaleCode;
    }
    if (typeof date === 'string' || !date) {
      date = date ? new Date(date) : new Date();
    }
    const safeLocales = Intl.DateTimeFormat.supportedLocalesOf(locales);
    return Intl.DateTimeFormat(safeLocales, options).format(date);
  };
}

/**
 * parse document title for @react-navigation NavigationContainer
 * @param  {Record<string, any> | undefined} options options from @react-navigation options
 * @param {Route<string, object | undefined> | undefined} route from @react-navigation options
 * @return {string} format string to Title - Domain/ThemeTitle
 */
export const titleFormatter = (
  options: Record<string, any> | undefined,
  route: Route<string, object | undefined> | undefined
) => {
  let title: string = options?.title ?? route?.name;
  title += theme?.name ? ` - ${theme.name}` : '';
  return title;
};

export const countryCodeToServiceChannel = (
  countryCode: CountryCode
): string => {
  switch (countryCode) {
    case 'SE':
      return 'FI-AIMO-ONE';
    case 'NO':
      return 'NO-AIMO-ONE';
    default:
      return 'FI-AIMO-ONE';
  }
};

export const countryCodeToLang = (countryCode?: CountryCode): Lang => {
  switch (countryCode) {
    case 'SE':
      return 'sv';
    case 'NO':
      return 'no';
    case 'FI':
      return 'fi';
    default:
      Logger.warn(`Getting country from countryCode ${countryCode}`);
      return 'en';
  }
};

export const countryCodeToCountry = (countryCode?: CountryCode): string => {
  switch (countryCode) {
    case 'FI':
      return 'Finland';
    case 'SE':
      return 'Sweden';
    case 'NO':
      return 'Norway';
    default:
      Logger.warn(
        `Getting country from countryCode ${countryCode} returns fallbackCountry: ${fallbackCountry}`
      );
      return fallbackCountry;
  }
};

export const countryToCountryCode = (country?: string): CountryCode => {
  switch (country) {
    case 'Finland':
      return 'FI';
    case 'Sweden':
      return 'SE';
    case 'Norway':
      return 'NO';
    default:
      Logger.warn(
        `Getting country from countryCode ${country} returns fallbackCountry: ${fallbackCountryCode}`
      );
      return fallbackCountryCode;
  }
};

const formatPriceWithCurrency = (
  priceInCents: number,
  countryCode: CountryCode
) => {
  return currencyFormatter((priceInCents || 0) / 100, countryCode);
};

const createPriceString = (
  priceInCents: number,
  countryCode: CountryCode,
  translationKey: string
) => {
  const price = formatPriceWithCurrency(priceInCents, countryCode);
  return i18n.t(translationKey, { price });
};

const isPriceValid = (price: number | null | undefined): price is number => {
  return price !== null && price !== undefined;
};

const BILLING_FREQUENCY_TRANSLATION_KEYS: Record<BillingFrequency, string> = {
  MONTHLY: 'price.monthlyPrice',
  QUARTERLY: 'price.quarterlyPrice',
  HALF_YEARLY: 'price.halfYearPrice',
  ANNUALLY: 'price.annualPrice',
  ONE_SHOT: '',
};

export const formatPriceForBillingFrequency = (
  price: number,
  countryCode: CountryCode,
  billingFrequency: BillingFrequency
): string => {
  const translationKey = BILLING_FREQUENCY_TRANSLATION_KEYS[billingFrequency];
  const formattedPrice = currencyFormatter(price, countryCode);
  return translationKey
    ? i18n.t(translationKey, { price: formattedPrice })
    : formattedPrice;
};

const getBillingFrequency = (price: Pricing | undefined): BillingFrequency => {
  if (!price || isPriceValid(price.monthlyPrice)) {
    return 'MONTHLY';
  } else if (isPriceValid(price.quarterlyPrice)) {
    return 'QUARTERLY';
  } else if (isPriceValid(price.halfYearPrice)) {
    return 'HALF_YEARLY';
  } else if (isPriceValid(price.annualPrice)) {
    return 'ANNUALLY';
  }
  return 'MONTHLY';
};

export type PmcPrice = {
  totalPrice: string;
  serviceFee?: string;
  totalPriceCents?: number;
  serviceFeeCents?: number;
};

export const getPriceForSalesOrder = (
  prices: Maybe<Pricing[]> | undefined,
  countryCode: CountryCode,
  startDate?: Date,
  selectedBillingFrequency: BillingFrequency = getBillingFrequency(
    getValidPrice(prices ?? [], startDate)
  )
): PmcPrice => {
  if (!prices?.length) {
    return { totalPrice: i18n.t('price.noPrice') };
  }

  const validPrice = getValidPrice(prices, startDate);
  if (!validPrice) {
    return { totalPrice: i18n.t('price.noPrice') };
  }

  const priceMap: Record<BillingFrequency, Maybe<number> | undefined> = {
    MONTHLY: validPrice.monthlyPrice,
    QUARTERLY: validPrice.quarterlyPrice,
    HALF_YEARLY: validPrice.halfYearPrice,
    ANNUALLY: validPrice.annualPrice,
    ONE_SHOT: undefined,
  };

  const selectedPrice = priceMap[selectedBillingFrequency];

  if (!isPriceValid(selectedPrice)) {
    return { totalPrice: i18n.t('price.noPrice') };
  }

  const isFree = selectedPrice === 0;
  const serviceFeeCents = validPrice.serviceFee ?? 0;
  const totalPriceCents = isFree ? 0 : selectedPrice + serviceFeeCents;
  return {
    totalPrice: createPriceString(
      totalPriceCents,
      countryCode,
      BILLING_FREQUENCY_TRANSLATION_KEYS[selectedBillingFrequency]
    ),
    totalPriceCents,
    ...(isFree
      ? {}
      : {
          serviceFee: createPriceString(
            serviceFeeCents,
            countryCode,
            'price.serviceFee'
          ),
          serviceFeeCents,
        }),
  };
};

export const isSalesOrderFree = (
  prices: Maybe<Pricing[]> | undefined,
  startDate?: Date
) => {
  if (!prices || prices?.length === 0) {
    return false;
  }
  const validPrice = getValidPrice(prices, startDate);
  if (validPrice?.thirtyDayPrice !== null) {
    return validPrice?.thirtyDayPrice === 0;
  } else if (validPrice?.monthlyPrice !== null) {
    return validPrice?.monthlyPrice === 0;
  } else if (validPrice?.quarterlyPrice !== null) {
    return validPrice?.quarterlyPrice === 0;
  } else if (validPrice?.halfYearPrice !== null) {
    return validPrice?.halfYearPrice === 0;
  } else if (validPrice?.annualPrice !== null) {
    return validPrice?.annualPrice === 0;
  }
  return false;
};

export const getValidPrice = (prices: Pricing[], startDate?: Date) => {
  const today = startDate ?? new Date();

  return [...prices]
    ?.sort((a, b) => {
      const dateA = new Date(a?.validity?.validFrom ?? 0).getTime();
      const dateB = new Date(b?.validity?.validFrom ?? 0).getTime();
      return dateB - dateA;
    })
    .find((price: Pricing) => {
      const { validity } = price;
      if (!validity?.validFrom) {
        return false;
      }

      const validFromDate = new Date(validity.validFrom);
      const validToDate = validity.validTo ? new Date(validity.validTo) : null;

      const isValidFrom =
        isSameDay(today, validFromDate) || isAfter(today, validFromDate);
      const isValidTo =
        !validToDate ||
        isSameDay(today, validToDate) ||
        isBefore(today, validToDate);

      return isValidFrom && isValidTo;
    });
};

export const getFlattenObject = (
  object: any,
  flattenedObject: any,
  flattenedKey?: any
) => {
  Object.keys(object).forEach((key: string) => {
    let newKey;
    if (flattenedKey === undefined) {
      newKey = key;
    } else {
      newKey = flattenedKey + '.' + key;
    }
    const value = object[key];
    if (typeof value === 'object') {
      getFlattenObject(value, flattenedObject, newKey);
    } else {
      flattenedObject[newKey] = value;
    }
  });
  return flattenedObject;
};

export const getParkingZoneName = (parkZone?: Maybe<ParkZone>) => {
  return `${parkZone?.name}${
    parkZone?.address?.city ? ', ' + parkZone?.address?.city : ''
  }`;
};

export const isRunningLocally = () => {
  return (
    config.dotEnvFilenameVar === 'env' || process.env.NODE_ENV === 'development'
  );
};

export const isDatadogAvailable = (): boolean => {
  // For now, we do not set up Datadog in Web browser neither in local environment.
  return !isWeb && !isRunningLocally();
};

export const splitAddress = (street: string) => {
  const streetObj: string[] = street
    .trim()
    .replace(/\s+/g, ' ')
    .split(/^([\p{L}\s]{1,30}?)\s(\d{1,9}?)$/u);
  return {
    streetName: streetObj[1] ?? streetObj[0],
    streetNumber: streetObj[2],
  };
};

export const getShouldAppUpdate = (
  mandatoryVersion: string,
  optionalVersion: string,
  currentVersion: string
) => {
  if (currentVersion === '') {
    return { mandatory: false, optional: false };
  }
  return {
    mandatory: compareVersions(mandatoryVersion, currentVersion) > 0,
    optional: compareVersions(optionalVersion, currentVersion) > 0,
  };
};

export const compareVersions = (version1: string, version2: string) => {
  const v1Parts = version1?.split('.').map(Number);
  const v2Parts = version2?.split('.').map(Number);

  for (let i = 0; i < Math.max(v1Parts?.length, v2Parts?.length); i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;

    if (v1Part < v2Part) {
      return -1;
    } else if (v1Part > v2Part) {
      return 1;
    }
  }

  return 0; // Both versions are equal
};

// Read the getVersion function from react-native-device-info and return the version.
// If version has format X.Y.Z-<env>.<hash>, return the version without the -<env>.<hash> part.
export const getFormattedVersion = () => {
  const version = isWeb ? packageJsonVersion : getVersion();
  return version.split('-')[0];
};

export const getCountryFromCurrency = (currency?: string) => {
  switch (currency) {
    case 'SEK':
      return 'SE';
    case 'EUR':
      return 'FI';
    case 'NOK':
      return 'NO';
  }
};

export const convertBase64DataToPDF = (
  base64PdfFile: string,
  type: string,
  sessionId: string
) => {
  if (isWeb) {
    const binary_string = window.atob(base64PdfFile);
    const bytes = new Uint8Array(base64PdfFile.length);
    for (let i = 0; i < base64PdfFile.length; i++) {
      bytes[i] = binary_string.charCodeAt(i);
    }
    const blob = new Blob([bytes.buffer], { type });
    const url = URL.createObjectURL(blob);
    return url;
  } else {
    const { dirs } = ReactNativeBlobUtil.fs;
    const dir = isIos ? dirs.CacheDir : dirs.DocumentDir;
    const path = `${dir}/${sessionId}.pdf`;
    return path;
  }
};

export const openPDF = (
  pdfUrl: string,
  base64PdfFile: string,
  type: string,
  errorCallback: (error: any) => void
) => {
  if (isWeb) {
    window.open(pdfUrl);
  } else {
    ReactNativeBlobUtil.fs
      .writeFile(pdfUrl, base64PdfFile, 'base64')
      .then(() => {
        if (isAndroid) {
          ReactNativeBlobUtil.android.actionViewIntent(pdfUrl, type);
        } else if (isIos) {
          ReactNativeBlobUtil.ios.openDocument(pdfUrl);
        }
      })
      .catch((error) => {
        errorCallback?.(error);
      });
  }
};

const mimeTypeToExtension: Record<string, string> = {
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
};

const getAttachmentFileName = (fileName?: string, mimeType?: string) => {
  const fallbackExtension = mimeType
    ? (mimeTypeToExtension[mimeType] ?? '')
    : '';
  const name = fileName?.trim() || `attachment${fallbackExtension}`;

  return name.replace(/[\\/:*?"<>|]/g, '_');
};

const getBlobFromBase64 = (base64File: string, mimeType: string) => {
  const binaryString = globalThis.atob(base64File);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.codePointAt(i) ?? 0;
  }

  return new Blob([bytes.buffer], { type: mimeType });
};

const saveBase64ToAndroidDownloads = async ({
  base64File,
  safeFileName,
  mimeType,
}: {
  base64File: string;
  safeFileName: string;
  mimeType: string;
}) => {
  const { dirs } = ReactNativeBlobUtil.fs;
  // Write to internal storage first; MediaCollection.copyToMediaStore copies
  // it into the user-visible Downloads collection
  const tempPath = `${dirs.DocumentDir}/${safeFileName}`;

  await ReactNativeBlobUtil.fs.writeFile(tempPath, base64File, 'base64');

  try {
    await ReactNativeBlobUtil.MediaCollection.copyToMediaStore(
      {
        name: safeFileName,
        parentFolder: '',
        mimeType,
      },
      'Download',
      tempPath
    );
  } finally {
    ReactNativeBlobUtil.fs.unlink(tempPath).catch(() => undefined);
  }
};

const saveBase64ToIosAndShare = async ({
  base64File,
  safeFileName,
}: {
  base64File: string;
  safeFileName: string;
}) => {
  const { dirs } = ReactNativeBlobUtil.fs;
  const path = `${dirs.DocumentDir}/${safeFileName}`;

  await ReactNativeBlobUtil.fs.writeFile(path, base64File, 'base64');
  await Share.share({
    url: `file://${path}`,
    title: safeFileName,
    message: safeFileName,
  });
};

const triggerWebDownload = ({
  url,
  safeFileName,
  target,
}: {
  url: string;
  safeFileName: string;
  target?: string;
}) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = safeFileName;
  if (target) {
    link.target = target;
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const openWebAttachmentFile = ({
  presignedUrl,
  base64File,
  fileUri,
  safeFileName,
  mimeType,
}: {
  presignedUrl?: string;
  base64File?: string;
  fileUri?: string;
  safeFileName: string;
  mimeType: string;
}) => {
  if (presignedUrl) {
    triggerWebDownload({ url: presignedUrl, safeFileName, target: '_blank' });
    return;
  }

  if (base64File) {
    const url = URL.createObjectURL(getBlobFromBase64(base64File, mimeType));
    triggerWebDownload({ url, safeFileName });
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return;
  }

  if (fileUri) {
    window.open(fileUri);
  }
};

const DOWNLOAD_HTTP_OK_MIN = 200;
const DOWNLOAD_HTTP_OK_MAX = 300;

export const assertDownloadStatus = (
  res: Awaited<ReturnType<typeof ReactNativeBlobUtil.fetch>>
) => {
  const status = res.info().status;
  if (status < DOWNLOAD_HTTP_OK_MIN || status >= DOWNLOAD_HTTP_OK_MAX) {
    throw new Error(`Download failed with status ${status}`);
  }
};

const shareFileOnIos = ({
  path,
  safeFileName,
}: {
  path: string;
  safeFileName: string;
}) =>
  Share.share({
    url: `file://${path}`,
    title: safeFileName,
    message: safeFileName,
  });

const downloadPresignedUrlToAndroid = ({
  presignedUrl,
  safeFileName,
  mimeType,
}: {
  presignedUrl: string;
  safeFileName: string;
  mimeType: string;
}) =>
  ReactNativeBlobUtil.config({
    addAndroidDownloads: {
      useDownloadManager: true,
      notification: true,
      title: safeFileName,
      description: safeFileName,
      mime: mimeType || 'application/octet-stream',
      mediaScannable: true,
      storeInDownloads: true,
    },
  })
    .fetch('GET', presignedUrl)
    .then((res) => {
      // DownloadManager surfaces most HTTP failures (403, 404, etc.) as
      // STATUS_FAILED → promise rejection, but not always for every edge case.
      // Verify the response status as a backstop so an expired presigned URL
      // never gets reported back as a success.
      assertDownloadStatus(res);
    });

const downloadPresignedUrlToIos = ({
  presignedUrl,
  safeFileName,
}: {
  presignedUrl: string;
  safeFileName: string;
}) => {
  const { dirs } = ReactNativeBlobUtil.fs;
  const localPath = `${dirs.CacheDir}/${Date.now()}-${safeFileName}`;

  return ReactNativeBlobUtil.config({ path: localPath })
    .fetch('GET', presignedUrl)
    .then(async (res) => {
      const path = res.path();
      try {
        // Without this, a 403/expired presigned URL's error body (S3 XML)
        // gets saved to disk and offered to the user via the share sheet.
        assertDownloadStatus(res);
        await shareFileOnIos({ path, safeFileName });
      } finally {
        ReactNativeBlobUtil.fs.unlink(path).catch(() => undefined);
      }
    });
};

const downloadPresignedUrlToNative = ({
  presignedUrl,
  safeFileName,
  mimeType,
}: {
  presignedUrl: string;
  safeFileName: string;
  mimeType: string;
}) => {
  if (isAndroid) {
    return downloadPresignedUrlToAndroid({
      presignedUrl,
      safeFileName,
      mimeType,
    });
  }

  if (isIos) {
    return downloadPresignedUrlToIos({ presignedUrl, safeFileName });
  }

  return Promise.resolve();
};

const saveBase64ToNative = ({
  base64File,
  safeFileName,
  mimeType,
}: {
  base64File: string;
  safeFileName: string;
  mimeType: string;
}) =>
  isAndroid
    ? saveBase64ToAndroidDownloads({ base64File, safeFileName, mimeType })
    : saveBase64ToIosAndShare({ base64File, safeFileName });

const getNativeShareUri = (fileUri: string) => {
  if (isIos && !fileUri.startsWith('file://')) {
    return `file://${fileUri}`;
  }

  return fileUri;
};

const shareNativeAttachmentFile = ({
  fileUri,
  safeFileName,
}: {
  fileUri: string;
  safeFileName: string;
}) =>
  Share.share({
    url: getNativeShareUri(fileUri),
    title: safeFileName,
    message: safeFileName,
  });

export const openAttachmentFile = async ({
  presignedUrl,
  base64File,
  fileUri,
  fileName,
  mimeType,
  errorCallback,
}: {
  presignedUrl?: string;
  base64File?: string;
  fileUri?: string;
  fileName?: string;
  mimeType: string;
  errorCallback?: (error: unknown) => void;
}): Promise<void> => {
  const safeFileName = getAttachmentFileName(fileName, mimeType);

  if (isWeb) {
    openWebAttachmentFile({
      presignedUrl,
      base64File,
      fileUri,
      safeFileName,
      mimeType,
    });
    return;
  }

  if (!presignedUrl && !base64File && !fileUri) {
    errorCallback?.(new Error('No file content provided to download'));
    return;
  }

  try {
    if (presignedUrl) {
      await downloadPresignedUrlToNative({
        presignedUrl,
        safeFileName,
        mimeType,
      });
      return;
    }

    if (base64File) {
      await saveBase64ToNative({ base64File, safeFileName, mimeType });
      return;
    }

    if (fileUri) {
      await shareNativeAttachmentFile({ fileUri, safeFileName });
    }
  } catch (error) {
    errorCallback?.(error);
  }
};

type ThrottledFunction<T> = (...args: any[]) => T;

// Module-level mutable ref holding active throttle timer handles keyed by id.
// Not reactive state — nothing subscribes to it; it only tracks setTimeout
// handles for `throttleWithId`.
export const throttleTimers: ThrottleTimer = {};

/**
 * Throttle logic that delays the execution of a function based on a given ID.
 * @param id The ID used to identify the throttle logic.
 * @param func The function to throttle.
 * @param delay The throttle period in milliseconds.
 * @returns A throttled version of the function.
 */
export const throttleWithId = <T>(
  id: string,
  func: ThrottledFunction<T>,
  delay: number
) => {
  return (...args: any[]) => {
    if (!throttleTimers[id]) {
      // If there's no throttle timeout set, execute the function immediately
      const result = func.apply(this, args);
      // Set a new throttle timeout
      throttleTimers[id] = setTimeout(() => {
        // Reset the throttle timeout
        throttleTimers[id] = undefined;
      }, delay);
      return result;
    }
  };
};

export const vehicleLicensePlateCountryCode = (vehicle: Vehicle) => {
  const combinedLicensePlateCountryCode = (
    licensePlate: string,
    countryCode?: Maybe<VehicleCountryCode>
  ) => `${countryCode || ''}:${licensePlate}`;

  return combinedLicensePlateCountryCode(
    vehicle.licensePlate.plateText,
    vehicle.licensePlate.countryCode
  );
};

export const getCompanyBenefitsUids = (
  companyBenefitsValue: CompanyBenefit[]
) => {
  return !Array.isArray(companyBenefitsValue)
    ? []
    : (companyBenefitsValue
        ?.map((companyBenefit) => companyBenefit?.uid)
        ?.filter((uid) => !!uid) as string[]);
};

export const getModalButtonType = (
  modalType: string
): 'secondary' | 'tertiary' => {
  const secondaryModals = ['blockVehicleDeletion', 'anprPaymentInfo'];
  if (secondaryModals.includes(modalType)) {
    return 'secondary';
  }
  return 'tertiary';
};

export const canOpenChatbot = (
  urlString: string,
  country: CountryCode | undefined | null
): boolean => {
  if (!urlString || !country) {
    return false;
  }
  const urls = parseFeatureFlagObject<
    Record<CountryCode, Record<Lang, string>>
  >(urlString, 'chatbot_urls');
  return Boolean(urls[country] && Object.keys(urls[country]).length > 0);
};

export const truncateText = (text: string, maxLength: number) => {
  if (!text) {
    return '';
  }
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

/**
 * Formats an address object into a string
 * @param addr Address object with optional street, number, and city properties
 * @returns Formatted address string or empty string if no street is available
 */
export const getFormattedAddress = (addr?: AddressProps | null) => {
  if (!addr?.street) {
    return '';
  }

  let result = addr.street;

  if (addr.number) {
    result += ' ' + addr.number;
  }

  if (addr.city) {
    result += ', ' + addr.city;
  }

  return result;
};

export const getMidnightUTCFromISOString = (isoString: string) => {
  let localDate = new Date(isoString);
  let utcDate = new Date(
    localDate.getTime() - localDate.getTimezoneOffset() * 60000
  );
  return utcDate.toISOString();
};

export const emptyQuery = {
  data: undefined,
  loading: false,
  error: undefined,
};
